/**
 * Which PROduce native app is running: the crew app or the producer app.
 *
 * Two store apps now share this package (and one Supabase backend). Code that
 * behaves differently per app branches on an AppId rather than on the store
 * name: that name changes per build environment ("PROduce (Staging)",
 * "PROduce (Dev)", …) and is about to change for the crew app as well, so a
 * string comparison against it would silently stop matching.
 */

import { APP_NAME } from "./constants";

/** Every app id, in a stable order — the single list the type derives from. */
export const APP_IDS = ["crew", "producer"] as const;

export type AppId = (typeof APP_IDS)[number];

/**
 * The store / home-screen name of each app.
 *
 * Built on APP_NAME so the brand cannot drift between the two names and the
 * rest of the platform. APP_NAME itself stays "PROduce": it is the brand the
 * web app and emails use, not an app name.
 *
 * The crew rename to "PROduce Crew" ships with the next NATIVE crew build
 * (the store name lives in apps/mobile/app.config.ts and cannot change over
 * OTA). Until that build is live, the installed crew app still reads
 * "PROduce" — copy that tells crew to "open PROduce Crew" is premature before
 * then.
 */
export const APP_DISPLAY_NAMES = {
  crew: `${APP_NAME} Crew`,
  producer: `${APP_NAME} Producer`,
} as const satisfies Readonly<Record<AppId, string>>;

/**
 * Narrow an untyped value (an Expo config `extra` field, an env var, a stored
 * preference) to an AppId once, at the boundary, instead of casting it. Exact
 * match only: "Crew" or " crew" is a misconfiguration to surface, not to
 * guess at.
 */
export function isAppId(value: unknown): value is AppId {
  return typeof value === "string" && (APP_IDS as readonly string[]).includes(value);
}
